(() => {
  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  const pageType = document.body?.dataset.pageType || "unknown";
  let themeStrings = {};
  try {
    const themeStringsNode = document.getElementById("EagleLineThemeStrings");
    if (themeStringsNode) themeStrings = JSON.parse(themeStringsNode.textContent);
  } catch {
    themeStrings = {};
  }
  const themeString = (key, fallback = "") => {
    const value = themeStrings[key];
    return typeof value === "string" && value ? value : fallback;
  };
  const publishEagleLineEvent = (name, parameters = {}) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== "function") return false;
    try {
      const result = publish.call(window.Shopify.analytics, name, parameters);
      result?.catch?.(() => {});
      return true;
    } catch {
      return false;
    }
  };

  const observeQualifiedVisibility = (element, delay, qualifies, callback) => {
    if (!element || !("IntersectionObserver" in window)) return () => {};
    let timer = 0;
    let qualifying = false;
    let latestEntry = null;
    let attempts = 0;
    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = 0;
    };
    const cleanup = () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    const schedule = () => {
      if (timer || !qualifying || document.visibilityState !== "visible") return;
      timer = window.setTimeout(() => {
        timer = 0;
        if (!qualifying || document.visibilityState !== "visible" || !element.isConnected) return;
        const completed = callback();
        if (completed !== false) {
          cleanup();
          return;
        }
        attempts += 1;
        if (attempts < 10) schedule();
        else cleanup();
      }, attempts ? 500 : delay);
    };
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      latestEntry = entry || null;
      qualifying = Boolean(entry && qualifies(entry));
      if (!qualifying) {
        clearTimer();
        return;
      }
      schedule();
    }, { threshold: [0, .01, .25, .5, .75] });
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearTimer();
        return;
      }
      qualifying = Boolean(latestEntry && qualifies(latestEntry));
      schedule();
    };
    observer.observe(element);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return cleanup;
  };

  const viewedCtaPlacements = new Set();
  document.querySelectorAll("[data-purchase-cta]").forEach((cta) => {
    const placement = cta.dataset.ctaPlacement;
    if (!placement) return;
    observeQualifiedVisibility(
      cta,
      500,
      (entry) => entry.isIntersecting && entry.intersectionRatio >= .5,
      () => {
        if (viewedCtaPlacements.has(placement)) return true;
        const published = publishEagleLineEvent("eagleline_cta_view", { placement, page_type: pageType });
        if (published) viewedCtaPlacements.add(placement);
        return published;
      },
    );
    cta.addEventListener("click", (event) => {
      if (!event.isTrusted) return;
      publishEagleLineEvent("eagleline_cta_click", {
        placement,
        destination: cta.dataset.ctaDestination,
        page_type: pageType,
      });
    });
  });

  let purchaseFormViewed = false;
  document.querySelectorAll("[data-product-form]").forEach((formRoot) => {
    const form = formRoot.querySelector("form") || formRoot;
    observeQualifiedVisibility(
      form,
      1000,
      (entry) => entry.isIntersecting && entry.intersectionRatio >= .5,
      () => {
        if (purchaseFormViewed) return true;
        const published = publishEagleLineEvent("eagleline_purchase_form_view", { page_type: pageType });
        if (published) purchaseFormViewed = true;
        return published;
      },
    );
  });

  const viewedContentSections = new Set();
  document.querySelectorAll("[data-content-section]").forEach((section) => {
    const sectionName = section.dataset.contentSection;
    if (!sectionName) return;
    observeQualifiedVisibility(
      section,
      1000,
      (entry) => entry.isIntersecting && entry.intersectionRatio >= .5,
      () => {
        if (viewedContentSections.has(sectionName)) return true;
        const published = publishEagleLineEvent("eagleline_content_view", {
          section_name: sectionName,
          page_type: pageType,
        });
        if (published) viewedContentSections.add(sectionName);
        return published;
      },
    );
  });

  const openedFaqIds = new Set();
  document.querySelectorAll("details[data-faq-id]").forEach((details) => {
    const faqId = details.dataset.faqId;
    const summary = details.querySelector("summary");
    if (!faqId || !summary) return;
    summary.addEventListener("click", (event) => {
      if (!event.isTrusted || details.open || openedFaqIds.has(faqId)) return;
      window.requestAnimationFrame(() => {
        if (!details.open || openedFaqIds.has(faqId)) return;
        const published = publishEagleLineEvent("eagleline_faq_open", {
          faq_id: faqId,
          page_type: pageType,
        });
        if (published) openedFaqIds.add(faqId);
      });
    });
  });

  document.addEventListener("click", (event) => {
    if (!event.isTrusted) return;
    if (event.target.closest?.("[data-selection-gate]")) return;
    const checkoutControl = event.target.closest?.("[data-checkout-select], [data-accelerated-checkout]");
    if (!checkoutControl) return;
    const productForm = checkoutControl.closest("[data-product-form]");
    if (checkoutControl.matches("[data-accelerated-checkout]") && productForm?.dataset.selectionReady !== "true") return;
    const variantId = productForm?.querySelector("[data-variant-id]")?.value
      || checkoutControl.dataset.variantId;
    const parameters = {
      checkout_path: checkoutControl.dataset.checkoutPath || "accelerated_checkout",
      page_type: pageType,
    };
    if (variantId) parameters.variant_id = variantId;
    publishEagleLineEvent("eagleline_checkout_select", parameters);
  }, true);

  const initialize = () => {
  const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reducedMotion = motionPreference.matches;
  const reducedData = Boolean(
    navigator.connection?.saveData
    || window.matchMedia?.("(prefers-reduced-data: reduce)").matches,
  );

  const onMediaChange = (query, callback) => {
    if (query.addEventListener) query.addEventListener("change", callback);
    else query.addListener?.(callback);
  };

  onMediaChange(motionPreference, (event) => {
    reducedMotion = event.matches;
    if (reducedMotion) document.querySelectorAll("video").forEach((video) => video.pause());
    document.dispatchEvent(new CustomEvent("eagleline:media-state"));
  });

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute("aria-hidden") === "true") return false;
    if (!element.getClientRects().length) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  };

  const observeVisibility = (element, callback, options = {}) => {
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => callback(entry.isIntersecting, entry.intersectionRatio),
        options,
      );
      observer.observe(element);
      return () => observer.disconnect();
    }

    let frame = 0;
    const check = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const intersects = rect.bottom > 0 && rect.top < window.innerHeight;
      callback(intersects, intersects ? 1 : 0);
    };
    const requestCheck = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    window.addEventListener("scroll", requestCheck, { passive: true });
    window.addEventListener("resize", requestCheck, { passive: true });
    check();
    return () => {
      window.removeEventListener("scroll", requestCheck);
      window.removeEventListener("resize", requestCheck);
      if (frame) window.cancelAnimationFrame(frame);
    };
  };

  const safePlay = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.play().catch(() => {});
  };

  document.querySelectorAll("[data-header]").forEach((header) => {
    const update = () => header.classList.toggle("is-scrolled", window.scrollY > 12);
    update();
    window.addEventListener("scroll", update, { passive: true });
  });


  document.querySelectorAll("[data-hero-motion]").forEach((hero) => {
    let pointerX = 0;
    let pointerY = 0;
    let currentX = 0;
    let currentY = 0;
    let scrollY = 0;
    let frame = 0;

    const render = () => {
      frame = 0;
      if (reducedMotion) {
        currentX = 0;
        currentY = 0;
        hero.style.setProperty("--hero-x", "0px");
        hero.style.setProperty("--hero-y", "0px");
        return;
      }
      const goalY = pointerY + scrollY;
      currentX += (pointerX - currentX) * 0.14;
      currentY += (goalY - currentY) * 0.14;
      hero.style.setProperty("--hero-x", `${currentX.toFixed(2)}px`);
      hero.style.setProperty("--hero-y", `${currentY.toFixed(2)}px`);
      if (Math.abs(pointerX - currentX) > 0.04 || Math.abs(goalY - currentY) > 0.04) {
        frame = window.requestAnimationFrame(render);
      }
    };

    const requestRender = () => {
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    const updateScroll = () => {
      if (reducedMotion) return requestRender();
      const rect = hero.getBoundingClientRect();
      const viewportProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      scrollY = (Math.max(0, Math.min(1, viewportProgress)) - 0.5) * 10;
      /* Scroll-out follow-up to the load swipe. Two drivers, set on the root
         so the hero, the press baton line and the spine track all read them:
         --hero-exit-edge starts at first scroll and sweeps the twin cobalt
         bars back across the pinned hero in the reverse direction of the
         intro, while --hero-exit waits out a 10% hero-height dead zone
         before the copy sinks/dissolves and the photo settles and dims.
         Both complete just as the pinned hero releases (40svh). */
      const exit = Math.max(0, Math.min(1, (window.scrollY - rect.height * 0.1) / (rect.height * 0.28)));
      document.documentElement.style.setProperty("--hero-exit", exit.toFixed(3));
      const edgeExit = Math.max(0, Math.min(1, window.scrollY / (rect.height * 0.38)));
      document.documentElement.style.setProperty("--hero-exit-edge", edgeExit.toFixed(3));
      requestRender();
    };

    hero.addEventListener("pointermove", (event) => {
      if (reducedMotion || event.pointerType === "touch") return;
      const rect = hero.getBoundingClientRect();
      pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
      pointerY = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
      requestRender();
    });
    hero.addEventListener("pointerleave", () => {
      pointerX = 0;
      pointerY = 0;
      requestRender();
    });
    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll, { passive: true });
    onMediaChange(motionPreference, updateScroll);
    updateScroll();
  });

  /* Bar-linked intro: instead of a fixed time stagger, each copy element
     reveals (rise + fade) at the moment the intro swipe bars physically
     cross it. The bars literally write the copy in. Without JS or under
     reduced motion the CSS time-staggered entrance runs unchanged. */
  document.querySelectorAll(".el-v63-hero-full").forEach((hero) => {
    const edge = hero.querySelector(".el-hero-edge");
    const copy = hero.querySelector(".el-v6-hero-copy, .el-v63-hero-copy");
    /* The bar-linked version reads live geometry each animation frame. The
       compact layout keeps the same entrance via the CSS stagger instead,
       avoiding forced-layout work on phones and portrait tablets. */
    const compactViewport = window.matchMedia("(max-width: 920px)").matches;
    if (!edge || !copy || reducedMotion || compactViewport) return;
    const items = [...copy.children];
    if (!items.length) return;
    hero.classList.add("bar-linked");
    const scale = Math.min(1, window.innerWidth / 1440);
    const offsets = [40, 170, 300, 420, 510, 590].map((v) => v * scale);
    const started = performance.now();
    const tick = (now) => {
      const barX = edge.getBoundingClientRect().right;
      let pending = false;
      items.forEach((el, index) => {
        if (el.classList.contains("is-in")) return;
        const trigger = el.getBoundingClientRect().left + (offsets[index] || offsets[offsets.length - 1]);
        if (barX >= trigger || now - started > 2600) {
          el.classList.add("is-in");
        } else {
          pending = true;
        }
      });
      if (pending && now - started < 2800) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

  /* Shared carousel: dot anchors remain useful without JS; enhanced controls
     move one centered card at a time and keep the active state synchronized. */
  document.querySelectorAll("[data-highlights]").forEach((section) => {
    const track = section.querySelector("[data-hl-track]");
    const slides = [...section.querySelectorAll("[data-hl-slide]")];
    const dots = [...section.querySelectorAll("[data-hl-dot]")];
    let previous = null;
    let next = null;
    if (!track || !slides.length) return;
    if (!reducedMotion) track.classList.add("is-live");
    const timerText = section.querySelector(".el-v63-hl-symbol text");
    let timerFrame = 0;
    let timerWasActive = false;
    let frame = 0;
    let currentIndex = -1;
    let manualStartIndex = -1;
    let manualIntentTimer = 0;
    let pointerStart = null;
    let ignoreSlideClickUntil = 0;
    const clearManualIntent = () => {
      window.clearTimeout(manualIntentTimer);
      manualIntentTimer = 0;
      manualStartIndex = -1;
    };
    const syncControls = (index) => {
      [[previous, index <= 0], [next, index >= slides.length - 1]].forEach(([control, disabled]) => {
        if (!control) return;
        control.disabled = disabled;
        control.setAttribute("aria-disabled", String(disabled));
      });
    };
    const update = () => {
      frame = 0;
      /* Cards are narrower than the track, so the active slide is the one
         whose center is closest to the track's visible center. */
      const center = track.getBoundingClientRect().left + track.clientWidth / 2;
      let index = 0;
      let best = Infinity;
      slides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < best) { best = distance; index = i; }
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle("is-active", active);
        if (active) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
      slides.forEach((slide, i) => slide.classList.toggle("is-active", i === index));
      syncControls(index);
      if (manualStartIndex >= 0 && index !== manualStartIndex) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "image",
          placement: "mid_page",
          action: "gallery_select",
        });
        clearManualIntent();
      }
      currentIndex = index;
      /* Slide 3's timer counts up to 00:05 like a stopwatch each time its
         card centers; the arc sweep is CSS on the same activation. */
      const timerActive = Boolean(slides[index] && slides[index].matches(".el-v63-hl-icon.is-active"));
      if (timerText && !reducedMotion) {
        if (timerActive && !timerWasActive) {
          window.cancelAnimationFrame(timerFrame);
          const started = performance.now();
          const tick = (now) => {
            const t = Math.max(0, Math.min(1, (now - started - 150) / 1200));
            timerText.textContent = "00:0" + Math.min(5, Math.ceil(t * 5));
            if (t < 1) timerFrame = window.requestAnimationFrame(tick);
          };
          timerFrame = window.requestAnimationFrame(tick);
        } else if (!timerActive && timerWasActive) {
          window.cancelAnimationFrame(timerFrame);
          timerText.textContent = "00:05";
        }
      }
      timerWasActive = timerActive;
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const markManualIntent = (event) => {
      if (!event.isTrusted) return;
      manualStartIndex = currentIndex;
      window.clearTimeout(manualIntentTimer);
      manualIntentTimer = window.setTimeout(clearManualIntent, 2000);
    };
    const scrollToIndex = (index, event) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      if (targetIndex === currentIndex) return;
      const target = slides[targetIndex];
      const trackRect = track.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      markManualIntent(event);
      track.scrollTo({
        left: track.scrollLeft + targetRect.left + targetRect.width / 2
          - trackRect.left - track.clientWidth / 2,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    };
    const beginPointer = (event) => {
      markManualIntent(event);
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        scrollLeft: track.scrollLeft,
        moved: false,
      };
    };
    const trackPointer = (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      if (Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 8) {
        pointerStart.moved = true;
      }
    };
    const finishPointer = (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      if (pointerStart.moved || Math.abs(track.scrollLeft - pointerStart.scrollLeft) > 4) {
        ignoreSlideClickUntil = Date.now() + 350;
      }
      pointerStart = null;
    };
    track.addEventListener("pointerdown", beginPointer, { passive: true });
    track.addEventListener("pointermove", trackPointer, { passive: true });
    track.addEventListener("pointerup", finishPointer, { passive: true });
    track.addEventListener("pointercancel", finishPointer, { passive: true });
    track.addEventListener("wheel", markManualIntent, { passive: true });
    track.addEventListener("keydown", (event) => {
      if (["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(event.key)) {
        markManualIntent(event);
      }
    });
    previous = section.querySelector("[data-carousel-prev]");
    next = section.querySelector("[data-carousel-next]");
    previous?.addEventListener("click", (event) => scrollToIndex(currentIndex - 1, event));
    next?.addEventListener("click", (event) => scrollToIndex(currentIndex + 1, event));
    dots.forEach((dot, index) => dot.addEventListener("click", (event) => {
      event.preventDefault();
      scrollToIndex(index, event);
    }));
    slides.forEach((slide, index) => slide.addEventListener("click", (event) => {
      if (Date.now() < ignoreSlideClickUntil || index === currentIndex) return;
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      if (Math.min(trackRect.right, slideRect.right) <= Math.max(trackRect.left, slideRect.left)) return;
      scrollToIndex(currentIndex + (index < currentIndex ? -1 : 1), event);
    }));
    track.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Universal text reveal: every text element outside the chrome and the
     zones with their own choreography rises in once as it enters the
     viewport and never hides again. Leaf-level text only (a block whose
     descendants carry text reveals as one unit). Armed state is applied by
     JS only when motion is allowed; otherwise everything is simply visible. */
  if (!reducedMotion) {
    const revealZones = document.querySelectorAll("main, .el-footer");
    const revealExclude = ".el-v63-hero, .el-v63-hl-track, .el-v63-patent-gallery, .el-v63-five, .el-v63-diff-rows, .el-spine, .el-island, .el-header, .el-v63-offer-bar";
    const revealSelector = ".el-kicker, h1, h2, h3, h4, h5, p, li, figcaption, summary, dt, dd";
    const revealItems = [];
    revealZones.forEach((zone) => {
      zone.querySelectorAll(revealSelector).forEach((el) => {
        if (el.closest(revealExclude)) return;
        if (el.querySelector(revealSelector)) return; // keep reveals leaf-level
        if (el.closest("details:not([open])")) return; // collapsed content stays ready
        if (el.offsetParent === null) return; // hidden until shown by other logic
        revealItems.push(el);
      });
    });
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0 });
    const revealSiblings = new Map();
    revealItems.forEach((el) => {
      const parent = el.parentElement;
      const index = revealSiblings.get(parent) || 0;
      revealSiblings.set(parent, index + 1);
      el.style.setProperty("--reveal-delay", `${Math.min(index * 70, 350)}ms`);
      el.classList.add("el-reveal");
      revealObserver.observe(el);
    });
  }

  /* Hide the floating header while the purchase chapter itself is on screen,
     then restore it when the customer scrolls above or below that chapter. */
  document.querySelectorAll("[data-island]").forEach((island) => {
    const heroAnchor = document.querySelector(".el-hero-pin") || document.querySelector(".el-v63-hero");
    const header = document.querySelector("[data-header]");
    const productGallery = document.querySelector("[data-product-gallery]")
      || document.querySelector("#eagleline-buy");
    const productRegion = productGallery?.closest("section") || productGallery;
    let visible = false;
    let frame = 0;
    const setVisible = (nextVisible) => {
      if (nextVisible === visible) return;
      visible = nextVisible;
      island.classList.toggle("is-visible", visible);
      island.setAttribute("aria-hidden", String(!visible));
      island.toggleAttribute("inert", !visible);
    };
    const update = () => {
      frame = 0;
      const vh = window.innerHeight;
      const productRect = productRegion?.getBoundingClientRect();
      const productInView = Boolean(productRect && productRect.top < vh && productRect.bottom > 0);
      const openingCleared = heroAnchor
        ? heroAnchor.getBoundingClientRect().bottom <= vh * (visible ? .16 : .1)
        : pageType === "product" && ((header?.getBoundingClientRect().bottom || 0) <= 0 || window.scrollY > 96);
      setVisible(openingCleared && !productInView);
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  document.querySelectorAll("[data-spine]").forEach((spine) => {
    const progressEl = spine.querySelector(".el-spine-progress");
    const tipEl = spine.querySelector(".el-spine-tip");
    const labels = {
      top: themeString("spine_top", "Top"),
      "why-line": themeString("spine_why", "Why a line helps"),
      ritual: themeString("spine_ritual", "How to use"),
      story: themeString("spine_story", "Story"),
      technology: themeString("spine_technology", "Mechanism"),
      makuake: themeString("spine_makuake", "Makuake history"),
      "eagleline-buy": themeString("spine_buy", "Choose a product"),
    };
    /* Interstitial moments get no spine node: the highlights carousel, the
       two posters and the breather are transitions, not chapters. The FAQ
       is folded into the buy node, which carries the page's tail end. */
    const spineSkip = new Set(["highlights", "poster-line", "form", "poster-score", "faq"]);
    const chapters = [...document.querySelectorAll("main section[id]")].filter((section) => !spineSkip.has(section.id));
    const count = chapters.length;
    /* Where the spine dot conceptually "is" for a chapter: the section top
       sits just under the dynamic island when it is showing, otherwise with
       a small gap — measured live so framed sections never sit under chrome.
       Also precompute which chapters sit on dark or cobalt backgrounds so
       the spine can switch to its light scheme there. */
    const focusOffset = () => {
      const island = document.querySelector("[data-island]");
      if (island && island.classList.contains("is-visible")) {
        return island.getBoundingClientRect().bottom + 16;
      }
      return 24;
    };
    const chapterIsDark = chapters.map((chapter) => {
      const bg = getComputedStyle(chapter).backgroundColor.match(/[\d.]+/g);
      if (!bg || bg.length < 3 || (bg.length > 3 && parseFloat(bg[3]) === 0)) return false;
      const luminance = (0.2126 * bg[0] + 0.7152 * bg[1] + 0.0722 * bg[2]) / 255;
      return luminance < 0.45;
    });
    const nodes = chapters.map((chapter) => {
      const text = labels[chapter.id] || chapter.id;
      const node = document.createElement("button");
      node.type = "button";
      node.className = "el-spine-node";
      node.setAttribute("aria-label", themeString("spine_go_to", "Go to __SECTION__").replace("__SECTION__", text));
      const label = document.createElement("span");
      label.className = "el-spine-label";
      label.setAttribute("aria-hidden", "true");
      label.textContent = text;
      node.appendChild(label);
      node.addEventListener("click", () => {
        const top = chapter.getBoundingClientRect().top + window.scrollY;
        const target = Math.max(0, top - focusOffset());
        window.scrollTo({ top: target, behavior: reducedMotion ? "auto" : "smooth" });
      });
      spine.appendChild(node);
      return node;
    });

    let idleTimer = 0;
    const armIdle = () => {
      window.clearTimeout(idleTimer);
      spine.classList.remove("labels-idle");
      idleTimer = window.setTimeout(() => spine.classList.add("labels-idle"), 1200);
    };
    const setExpanded = (on) => {
      spine.classList.toggle("is-expanded", on);
      if (on) {
        window.clearTimeout(idleTimer);
        spine.classList.remove("labels-idle");
      } else {
        armIdle();
      }
    };
    window.addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      const near = event.clientX <= 96;
      if (near !== spine.classList.contains("is-expanded")) setExpanded(near);
    }, { passive: true });

    let frame = 0;
    let lastActive = -1;
    const update = () => {
      frame = 0;
      const scrollMax = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / scrollMax));
      /* Every chapter gets its natural anchor (its framed scroll position),
         including the last one: forcing the final anchor to 1 desynced the
         tip from the final sections' dots. The tip rests on the last node
         from that section's framed position to the page bottom. */
      const anchors = chapters.map((chapter, index) => {
        if (index === 0) return 0;
        const top = chapter.getBoundingClientRect().top + window.scrollY;
        return Math.min(1, Math.max(0, (top - focusOffset()) / scrollMax));
      });
      let segment = 0;
      for (let index = 0; index < count - 1; index += 1) {
        if (p >= anchors[index]) segment = index;
      }
      segment = Math.min(segment, count - 2);
      const a0 = anchors[segment];
      const a1 = anchors[segment + 1];
      const local = a1 > a0 ? Math.min(1, Math.max(0, (p - a0) / (a1 - a0))) : 1;
      const spinePos = Math.min(1, count > 1 ? (segment + local) / (count - 1) : 0);
      const activeIndex = Math.round(spinePos * (count - 1));
      if (activeIndex !== lastActive) {
        lastActive = activeIndex;
        spine.classList.toggle("is-inverted", chapterIsDark[activeIndex] === true);
        if (!spine.classList.contains("is-expanded")) armIdle();
      }
      if (progressEl) progressEl.style.height = `${spinePos * 100}%`;
      if (tipEl) tipEl.style.top = `${spinePos * 100}%`;
      nodes.forEach((node, index) => {
        const pos = count > 1 ? index / (count - 1) : 0;
        node.style.top = `${pos * 100}%`;
        node.classList.toggle("is-passed", spinePos >= pos - 0.001);
        node.classList.toggle("is-active", index === activeIndex);
      });
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    update();
  });

  /* Five-second steps: the three timestamped steps light in sequence with
     scroll. Without JS or under reduced motion they stay fully lit (CSS
     default); JS only "arms" the dim state when animation is allowed. */
  document.querySelectorAll("[data-five]").forEach((block) => {
    const stage = block.querySelector(".el-v63-five-stage");
    const steps = [...block.querySelectorAll(".el-v63-five-steps li")];
    if (!stage || reducedMotion) return;
    const thresholds = [0.15, 0.5, 0.85];
    block.classList.add("is-armed");
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh * 0.85 - rect.top) / (vh * 0.5)));
      steps.forEach((li, index) => li.classList.toggle("is-lit", p >= thresholds[index]));
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Angle diagram entry draw: the four angle lines draw themselves once when
     the diagram scrolls into view. Path lengths are measured so the dash
     animation needs no magic numbers; the overlay then fades back to the
     baked artwork. Skipped under reduced motion (image lines suffice). */
  document.querySelectorAll(".el-v63-angle-media").forEach((media) => {
    const draw = media.querySelector(".el-v63-angle-draw");
    if (!draw || reducedMotion) return;
    draw.querySelectorAll("path").forEach((path) => {
      const length = path.getTotalLength();
      path.style.setProperty("--len", `${length}px`);
      path.style.strokeDasharray = `${length}`;
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        media.classList.add("is-drawn");
        observer.disconnect();
      });
    }, { threshold: 0.35 });
    observer.observe(media);
  });

  /* Ball lane: a dimpled golf ball rolls the full page width left-to-right
     with scroll, rotating clockwise and drawing a cobalt line behind it —
     the ball marks the line. When the roll completes, the line flashes.
     Static ball, no mark, under reduced motion / no-JS. */
  document.querySelectorAll("[data-ball-lane]").forEach((lane) => {
    const ball = lane.querySelector(".el-v63-ball-lane-ball");
    const mark = lane.querySelector(".el-v63-ball-lane-mark");
    if (!ball || !mark || reducedMotion) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = lane.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      const startX = window.innerWidth - 48;
      const x = 4 + p * (startX - 4);
      const rotation = ((x - 4) / 69.1) * 360;
      ball.style.transform = `translate(${x.toFixed(1)}px, -50%) rotate(${rotation.toFixed(1)}deg)`;
      mark.style.width = `${(x + 11).toFixed(1)}px`;
      lane.classList.toggle("is-complete", p >= 0.985);
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Versus rows: staggered entry choreography. Each row rises in as it
     enters, the pain photo settles into its muted grade, the answer photo
     lifts, and the cobalt edge draws down beside the answer title. Final
     state is the CSS default; JS only arms the hidden start. */
  document.querySelectorAll(".el-v63-diff-rows").forEach((rows) => {
    if (reducedMotion) return;
    const items = [...rows.querySelectorAll(".el-v63-diff-row")];
    if (!items.length) return;
    rows.classList.add("is-armed");
    items.forEach((row, index) => { row.style.transitionDelay = `${index * 90}ms`; });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    items.forEach((row) => observer.observe(row));
  });

  document.querySelectorAll("[data-reveal]").forEach((element) => {
    if (reducedMotion) {
      element.classList.add("is-visible");
      return;
    }
    let stopObserving = () => {};
    stopObserving = observeVisibility(
      element,
      (inView) => {
        if (!inView) return;
        element.classList.add("is-visible");
        stopObserving();
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.15 },
    );
  });

  document.querySelectorAll("video[data-viewport-video]").forEach((video) => {
    if (video.closest("[data-mechanism-switcher]")) return;
    const shell = video.closest("[data-viewport-video-shell]") || video.parentElement;
    const toggle = shell?.querySelector("[data-viewport-video-toggle]");
    const toggleText = shell?.querySelector("[data-viewport-video-toggle-text]");
    const toggleIcon = shell?.querySelector("[data-viewport-video-toggle-icon]");
    const status = shell?.querySelector("[data-viewport-video-status]");
    let inView = false;
    let playbackEnabled = !reducedMotion && !reducedData;
    let reducedMotionOverride = false;
    let lastReducedMotion = reducedMotion;

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.pause();

    const playbackRequested = () =>
      playbackEnabled && (!reducedMotion || reducedMotionOverride);
    const announce = (message) => {
      if (status) status.textContent = message;
    };
    const syncToggle = () => {
      const enabled = playbackRequested();
      const text = enabled
        ? themeString("ritual_pause_label", "Pause the line-drawing demonstration")
        : themeString("ritual_play_label", "Play the line-drawing demonstration");
      toggle?.setAttribute("aria-label", text);
      toggle?.setAttribute("data-media-playback", enabled ? "enabled" : "paused");
      if (toggleText) toggleText.textContent = enabled
        ? themeString("pause", "Pause")
        : themeString("play", "Play");
      if (toggleIcon) toggleIcon.textContent = enabled ? "Ⅱ" : "▶";
    };
    const syncPlayingState = () => {
      toggle?.setAttribute("data-media-playing", String(!video.paused));
    };
    const sync = () => {
      if (playbackRequested() && !document.hidden && inView && isVisible(video)) safePlay(video);
      else video.pause();
      syncToggle();
    };
    if (toggle) {
      toggle.hidden = false;
      toggle.disabled = false;
      toggle.addEventListener("click", (event) => {
        const enablePlayback = !playbackRequested();
        playbackEnabled = enablePlayback;
        reducedMotionOverride = enablePlayback && reducedMotion;
        if (!enablePlayback) video.pause();
        sync();
        if (event.isTrusted) {
          publishEagleLineEvent("eagleline_media_engage", {
            media_type: "video",
            placement: "mid_page",
            action: enablePlayback ? "video_play" : "video_pause",
          });
        }
        announce(enablePlayback
          ? themeString("ritual_playing", "The line-drawing demonstration is playing.")
          : themeString("ritual_paused", "The line-drawing demonstration is paused."));
      });
    }
    video.addEventListener("play", syncPlayingState);
    video.addEventListener("pause", syncPlayingState);
    observeVisibility(
      video,
      (visible) => {
        inView = visible;
        sync();
      },
      { threshold: 0.28 },
    );
    document.addEventListener("visibilitychange", sync);
    document.addEventListener("eagleline:media-state", () => {
      if (reducedMotion !== lastReducedMotion) {
        lastReducedMotion = reducedMotion;
        reducedMotionOverride = false;
        playbackEnabled = !reducedMotion && !reducedData;
        announce(
          reducedMotion
            ? themeString("ritual_reduced_motion", "The demonstration is paused for reduced motion.")
            : reducedData
              ? themeString("ritual_reduced_data", "Autoplay is off for reduced data.")
              : themeString("ritual_motion_resumed", "The demonstration is playing after reduced motion was turned off."),
        );
      }
      sync();
    });
    syncPlayingState();
    syncToggle();
  });

  document.querySelectorAll("[data-youtube-embed]").forEach((embed) => {
    const button = embed.querySelector("[data-youtube-play]");
    const videoId = embed.dataset.youtubeId;
    if (!button || !videoId) return;

    button.addEventListener("click", (event) => {
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "video_play",
        });
      }
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      iframe.title = themeString("lesson_video_title", "Kina Golf explains how to use a line on a golf ball when putting");
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      iframe.allowFullscreen = true;
      iframe.tabIndex = 0;
      button.replaceWith(iframe);
      iframe.focus();
    });
  });

  const gallerySequences = new WeakMap();
  const galleryImageLoads = new Map();
  const GALLERY_MAIN_SIZES = "(max-width: 820px) min(calc(100vw - 32px), 680px), (max-width: 1100px) and (orientation: portrait) min(calc(100vw - 48px), 920px), (max-width: 1100px) 48vw, 640px";
  const galleryControlSources = (control) => {
    const fallback = control?.dataset.galleryThumb || "";
    const midSrc = control?.dataset.galleryMidSrc || "";
    const fullSrc = control?.dataset.galleryFullSrc || fallback;
    const declaredFullWidth = Number(control?.dataset.galleryWidth);
    const fullWidth = Number.isInteger(declaredFullWidth) && declaredFullWidth > 0
      ? declaredFullWidth
      : 1200;
    const src = fullSrc || midSrc || fallback;
    const srcset = midSrc && fullSrc ? `${midSrc} 768w, ${fullSrc} ${fullWidth}w` : "";
    return {
      src,
      srcset,
      sizes: srcset ? GALLERY_MAIN_SIZES : "",
    };
  };

  const loadGalleryImage = (media) => {
    const imageMedia = typeof media === "string" ? { src: media } : media;
    const source = imageMedia?.src;
    if (!source) return Promise.reject(new Error("Missing gallery image source"));
    const srcset = imageMedia.srcset || "";
    const sizes = imageMedia.sizes || "";
    const responsiveContext = srcset
      ? `${window.innerWidth || 0}@${window.devicePixelRatio || 1}`
      : "";
    const cacheKey = JSON.stringify([source, srcset, sizes, responsiveContext]);
    const cached = galleryImageLoads.get(cacheKey);
    if (cached) return cached;

    const request = new Promise((resolve, reject) => {
      const preload = new Image();
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("Gallery image load timed out"));
      }, 8000);
      const finish = async () => {
        if (settled) return;
        try {
          await preload.decode?.();
        } catch {}
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(preload);
      };
      preload.onload = finish;
      preload.onerror = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        reject(new Error("Gallery image failed to load"));
      };
      preload.decoding = "async";
      if (srcset) {
        preload.sizes = sizes || GALLERY_MAIN_SIZES;
        preload.srcset = srcset;
      }
      preload.src = source;
      if (preload.complete && preload.naturalWidth) window.queueMicrotask(finish);
    });

    galleryImageLoads.set(cacheKey, request);
    request.catch(() => {
      if (galleryImageLoads.get(cacheKey) === request) galleryImageLoads.delete(cacheKey);
    });
    return request;
  };

  const warmGalleryControl = (control) => {
    if (reducedData || !control?.dataset.galleryThumb) return;
    loadGalleryImage(galleryControlSources(control)).catch(() => {});
  };

  const warmGalleryNeighbors = (gallery, activeControl = null) => {
    if (reducedData) return;
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    const current = activeControl
      || controls.find((control) => control.getAttribute("aria-pressed") === "true");
    const currentIndex = controls.indexOf(current);
    if (currentIndex < 0) return;
    const warm = () => {
      warmGalleryControl(controls[currentIndex - 1]);
      warmGalleryControl(controls[currentIndex + 1]);
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(warm, { timeout: 1000 });
    } else {
      window.setTimeout(warm, 120);
    }
  };

  const galleryMeasuredInteractions = new WeakMap();

  const publishGalleryMediaEvent = (gallery, control, interactionSource, action = "gallery_select") => {
    const mediaId = control?.dataset.galleryMediaId;
    const mediaType = control?.dataset.galleryMediaType || "image";
    if (!mediaId || !interactionSource) return;
    const measured = galleryMeasuredInteractions.get(gallery) || new Set();
    const measurementKey = [mediaId, interactionSource, action].join(":");
    if (measured.has(measurementKey)) return;
    const published = publishEagleLineEvent("eagleline_media_engage", {
      media_type: mediaType,
      placement: pageType === "product" ? "product_page" : "purchase_section",
      action,
      media_id: mediaId,
      interaction_source: interactionSource,
    });
    if (!published) return;
    measured.add(measurementKey);
    galleryMeasuredInteractions.set(gallery, measured);
  };

  const syncGalleryVideoToggle = (video, toggle) => {
    if (!video || !toggle) return;
    const playing = !video.paused && !video.ended;
    toggle.classList.toggle("is-playing", playing);
    toggle.setAttribute("aria-label", playing
      ? themeString("gallery_pause_label", "Pause video")
      : themeString("gallery_play_label", "Play video"));
  };

  const setGalleryMedia = async (gallery, media, activeControl = null, options = {}) => {
    const frame = gallery.querySelector(".el-gallery-main");
    const mainImage = gallery.querySelector("[data-gallery-main]");
    const mainVideo = gallery.querySelector("[data-gallery-main-video]");
    const videoToggle = gallery.querySelector("[data-gallery-video-toggle]");
    const index = gallery.querySelector("[data-gallery-index]");
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    const mediaType = media?.mediaType === "video" ? "video" : "image";
    if (!frame || !mainImage || !mainVideo || !media?.src) return false;
    if (mediaType === "video" && !media.videoSrc) return false;
    const sequence = (gallerySequences.get(gallery) || 0) + 1;
    gallerySequences.set(gallery, sequence);

    const commit = () => {
      if (sequence !== gallerySequences.get(gallery)) return false;
      frame.dataset.galleryTreatment = media.treatment || "";

      if (mediaType === "video") {
        mainVideo.poster = media.src;
        mainVideo.setAttribute("aria-label", media.alt || themeString("gallery_fallback_alt", "EagleLine demonstration video"));
        if (mainVideo.dataset.gallerySource !== media.videoSrc) {
          mainVideo.pause();
          mainVideo.src = media.videoSrc;
          mainVideo.dataset.gallerySource = media.videoSrc;
          mainVideo.load();
        }
        mainImage.hidden = true;
        mainVideo.hidden = false;
        if (videoToggle) {
          videoToggle.hidden = false;
          syncGalleryVideoToggle(mainVideo, videoToggle);
        }
        if (!reducedMotion && options.autoplay !== false) {
          window.setTimeout(() => {
            if (mainVideo.hidden) return;
            mainVideo.play().catch(() => {});
          }, 0);
        }
      } else {
        mainVideo.pause();
        mainVideo.hidden = true;
        if (videoToggle) videoToggle.hidden = true;
        mainImage.hidden = false;
        mainImage.sizes = media.sizes || GALLERY_MAIN_SIZES;
        mainImage.srcset = media.srcset || media.src;
        mainImage.src = media.src;
        mainImage.decoding = "async";
        mainImage.alt = media.alt || "EagleLine";
        if (media.width) mainImage.width = Number(media.width);
        if (media.height) mainImage.height = Number(media.height);
        mainImage.dataset.galleryFit = media.fit || "cover";
        mainImage.style.opacity = "1";
      }

      if (index && activeControl?.dataset.galleryIndexValue) {
        index.textContent = activeControl.dataset.galleryIndexValue;
      }
      controls.forEach((control) => {
        const active = control === activeControl;
        control.classList.toggle("is-active", active);
        control.setAttribute("aria-pressed", String(active));
      });

      const controlStrip = activeControl?.parentElement;
      if (controlStrip && controlStrip.scrollWidth > controlStrip.clientWidth) {
        window.requestAnimationFrame(() => {
          const left = activeControl.offsetLeft - (controlStrip.clientWidth - activeControl.offsetWidth) / 2;
          controlStrip.scrollTo({
            left: Math.max(0, left),
            behavior: options.instant || reducedMotion ? "auto" : "smooth",
          });
        });
      }
      warmGalleryNeighbors(gallery, activeControl);
      return true;
    };

    gallery.setAttribute("aria-busy", "true");
    try {
      await loadGalleryImage(media);
      return commit();
    } catch {
      return false;
    } finally {
      if (sequence === gallerySequences.get(gallery)) gallery.removeAttribute("aria-busy");
    }
  };

  const galleryControlMedia = (control) => ({
    ...galleryControlSources(control),
    videoSrc: control?.dataset.galleryVideoSrc,
    mediaType: control?.dataset.galleryMediaType || "image",
    mediaId: control?.dataset.galleryMediaId,
    alt: control?.dataset.galleryAlt,
    width: control?.dataset.galleryWidth,
    height: control?.dataset.galleryHeight,
    fit: control?.dataset.galleryFit,
    treatment: control?.dataset.galleryTreatment,
  });

  const selectGalleryControl = (gallery, control, event, options = {}) => {
    if (!control) return Promise.resolve(false);
    if (control.getAttribute("aria-pressed") === "true") {
      if (gallery.getAttribute("aria-busy") === "true") {
        gallerySequences.set(gallery, (gallerySequences.get(gallery) || 0) + 1);
        gallery.removeAttribute("aria-busy");
      }
      return Promise.resolve(false);
    }
    const customerSelection = Boolean(event?.isTrusted || options.customerSelection);
    const interactionSource = options.interactionSource || "thumbnail";
    return setGalleryMedia(gallery, galleryControlMedia(control), control, options).then((committed) => {
      if (committed && customerSelection) {
        publishGalleryMediaEvent(gallery, control, interactionSource);
      }
      return committed;
    });
  };
  const initializeGallerySwipe = (gallery, controls) => {
    const frame = gallery.querySelector(".el-gallery-main");
    const mainImage = gallery.querySelector("[data-gallery-main]");
    const mainVideo = gallery.querySelector("[data-gallery-main-video]");
    if (!frame || !mainImage || !mainVideo || !("PointerEvent" in window)) return;

    const lockDistance = 6;
    const flickDistance = 10;
    const velocityWindow = 120;
    const flickProjection = 220;
    const commitVelocity = .18;
    const quickGestureWindow = 180;
    const edgeResistance = .18;
    let gesture = null;
    let track = null;
    let settleTimer = 0;
    let settleComplete = null;

    mainImage.draggable = false;
    mainVideo.draggable = false;

    const trackImage = (control) => {
      const sourceImage = control?.querySelector("img");
      const media = galleryControlSources(control);
      const source = media.src || sourceImage?.currentSrc || sourceImage?.src;
      if (!source) return document.createElement("span");
      const image = document.createElement("img");
      if (media.srcset) {
        image.sizes = media.sizes || GALLERY_MAIN_SIZES;
        image.srcset = media.srcset;
      }
      image.src = source;
      image.decoding = "async";
      image.alt = "";
      image.draggable = false;
      image.setAttribute("aria-hidden", "true");
      image.dataset.galleryFit = control?.dataset.galleryFit || "cover";
      image.dataset.galleryTreatment = control?.dataset.galleryTreatment || "";
      return image;
    };

    const removeTrack = () => {
      window.clearTimeout(settleTimer);
      settleTimer = 0;
      if (track && settleComplete) track.removeEventListener("transitionend", settleComplete);
      settleComplete = null;
      frame.classList.remove("is-swipe-tracking", "is-swipe-settling");
      track?.remove();
      track = null;
    };

    const waitForMainMediaPaint = async () => {
      if (!mainImage.hidden && typeof mainImage.decode === "function") {
        const imageReady = new Promise((resolve) => {
          if (mainImage.complete && mainImage.naturalWidth) {
            resolve();
            return;
          }
          let timeout = 0;
          const finish = () => {
            window.clearTimeout(timeout);
            mainImage.removeEventListener("load", finish);
            mainImage.removeEventListener("error", finish);
            resolve();
          };
          mainImage.addEventListener("load", finish, { once: true });
          mainImage.addEventListener("error", finish, { once: true });
          timeout = window.setTimeout(finish, 800);
        });
        try {
          await Promise.race([mainImage.decode(), imageReady]);
        } catch {
          await imageReady;
        }
      }
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });
    };

    const buildTrack = (activeIndex) => {
      removeTrack();
      warmGalleryControl(controls[activeIndex - 1]);
      warmGalleryControl(controls[activeIndex + 1]);
      track = document.createElement("div");
      track.className = "el-gallery-swipe-track";
      track.setAttribute("aria-hidden", "true");
      track.append(
        activeIndex > 0 ? trackImage(controls[activeIndex - 1]) : document.createElement("span"),
        trackImage(controls[activeIndex]),
        activeIndex < controls.length - 1 ? trackImage(controls[activeIndex + 1]) : document.createElement("span"),
      );
      frame.append(track);
      frame.classList.add("is-swipe-tracking");
    };

    const resetGesture = () => {
      gesture = null;
      removeTrack();
    };

    const recordGestureSamples = (activeGesture, event) => {
      let events = [];
      try {
        events = event.getCoalescedEvents?.() || [];
      } catch {}
      [...events, event].forEach((sample) => {
        if (!Number.isFinite(sample.clientX) || !Number.isFinite(sample.timeStamp)) return;
        const last = activeGesture.samples[activeGesture.samples.length - 1];
        if (last && sample.timeStamp < last.time) return;
        if (last && sample.timeStamp === last.time) {
          last.x = sample.clientX;
          return;
        }
        activeGesture.samples.push({ x: sample.clientX, time: sample.timeStamp });
      });
      const cutoff = event.timeStamp - velocityWindow;
      activeGesture.samples = activeGesture.samples.filter((sample) => sample.time >= cutoff);
      const clock = performance.now();
      activeGesture.clockSamples.push({ x: event.clientX, time: clock });
      activeGesture.clockSamples = activeGesture.clockSamples.filter(
        (sample) => sample.time >= clock - velocityWindow,
      );
    };

    const gestureReleaseVelocity = (activeGesture, releaseClock) => {
      const samples = activeGesture.clockSamples.filter(
        (sample) => sample.time >= releaseClock - velocityWindow,
      );
      if (samples.length < 2) return 0;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const elapsed = last.time - first.time;
      return elapsed > 0 ? (last.x - first.x) / elapsed : 0;
    };

    const finishGesture = (event, cancelled = false, useLastPoint = false) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const completedGesture = gesture;
      gesture = null;
      if (useLastPoint) {
        const clock = performance.now();
        completedGesture.clockSamples.push({ x: completedGesture.lastX, time: clock });
        completedGesture.clockSamples = completedGesture.clockSamples.filter(
          (sample) => sample.time >= clock - velocityWindow,
        );
      } else {
        recordGestureSamples(completedGesture, event);
      }
      const releaseX = !useLastPoint && Number.isFinite(event.clientX)
        ? event.clientX
        : completedGesture.lastX;
      const releaseY = !useLastPoint && Number.isFinite(event.clientY)
        ? event.clientY
        : completedGesture.lastY;
      completedGesture.deltaX = releaseX - completedGesture.startX;
      completedGesture.deltaY = releaseY - completedGesture.startY;
      if (frame.hasPointerCapture?.(completedGesture.pointerId)) {
        try {
          frame.releasePointerCapture(completedGesture.pointerId);
        } catch {}
      }
      if (cancelled || completedGesture.axis === "vertical") {
        removeTrack();
        return;
      }

      if (!completedGesture.axis) {
        const absoluteX = Math.abs(completedGesture.deltaX);
        const absoluteY = Math.abs(completedGesture.deltaY);
        if (
          Math.max(absoluteX, absoluteY) < lockDistance
          || absoluteX < lockDistance
          || absoluteY > absoluteX * 1.2
        ) {
          removeTrack();
          return;
        }
        completedGesture.axis = "horizontal";
        buildTrack(completedGesture.activeIndex);
      }

      if (!track) return;

      const width = frame.clientWidth || 1;
      const direction = completedGesture.deltaX < 0 ? 1 : -1;
      const targetIndex = completedGesture.activeIndex + direction;
      const hasTarget = targetIndex >= 0 && targetIndex < controls.length;
      const distance = Math.abs(completedGesture.deltaX);
      const distanceThreshold = Math.min(width * .16, 56);
      const releaseClock = performance.now();
      const recentVelocityX = gestureReleaseVelocity(completedGesture, releaseClock);
      const wholeGestureElapsed = releaseClock - completedGesture.startedAt;
      const wholeGestureVelocityX = wholeGestureElapsed > 0 && wholeGestureElapsed <= quickGestureWindow
        ? completedGesture.deltaX / wholeGestureElapsed
        : 0;
      const releaseVelocityX = Math.abs(wholeGestureVelocityX) > Math.abs(recentVelocityX)
        ? wholeGestureVelocityX
        : recentVelocityX;
      const projectedDistance = distance + Math.abs(releaseVelocityX) * flickProjection;
      const velocityCommits = distance >= flickDistance
        && Math.abs(releaseVelocityX) >= commitVelocity
        && Math.sign(releaseVelocityX) === Math.sign(completedGesture.deltaX)
        && projectedDistance >= distanceThreshold;
      const commits = hasTarget && (distance >= distanceThreshold || velocityCommits);
      const finalX = commits ? -direction * width : 0;
      const translatedX = hasTarget
        ? completedGesture.deltaX
        : completedGesture.deltaX * edgeResistance;
      track.style.transform = `translate3d(${translatedX}px, 0, 0)`;

      const complete = () => {
        if (commits) {
          selectGalleryControl(gallery, controls[targetIndex], event, {
            instant: true,
            interactionSource: "swipe",
          })
            .then(async (committed) => {
              if (committed) await waitForMainMediaPaint();
            })
            .finally(removeTrack);
          return;
        }
        removeTrack();
      };

      if (reducedMotion) {
        track.style.transform = `translate3d(${finalX}px, 0, 0)`;
        complete();
        return;
      }

      frame.classList.add("is-swipe-settling");
      const settlingTrack = track;
      let settled = false;
      const finishSettle = (transitionEvent = null) => {
        if (
          transitionEvent
          && (transitionEvent.target !== settlingTrack || transitionEvent.propertyName !== "transform")
        ) return;
        if (settled) return;
        settled = true;
        window.clearTimeout(settleTimer);
        settleTimer = 0;
        settlingTrack.removeEventListener("transitionend", finishSettle);
        if (settleComplete === finishSettle) settleComplete = null;
        complete();
      };
      settleComplete = finishSettle;
      settlingTrack.addEventListener("transitionend", finishSettle);
      window.requestAnimationFrame(() => {
        if (track !== settlingTrack) return;
        settlingTrack.style.transform = `translate3d(${finalX}px, 0, 0)`;
        settleTimer = window.setTimeout(finishSettle, 450);
      });
    };

    frame.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch") return;
      if (event.target.closest?.("[data-gallery-video-toggle]")) return;
      if (!event.isPrimary) {
        resetGesture();
        return;
      }
      if (event.button !== 0 || track || gallery.getAttribute("aria-busy") === "true") return;
      const activeIndex = controls.findIndex((control) => control.getAttribute("aria-pressed") === "true");
      if (activeIndex < 0) return;
      gesture = {
        pointerId: event.pointerId,
        activeIndex,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        deltaX: 0,
        deltaY: 0,
        samples: [{ x: event.clientX, time: event.timeStamp }],
        clockSamples: [{ x: event.clientX, time: performance.now() }],
        startedAt: performance.now(),
        axis: null,
      };
    });

    frame.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      gesture.lastX = event.clientX;
      gesture.lastY = event.clientY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);

      if (!gesture.axis) {
        if (Math.max(absoluteX, absoluteY) < lockDistance) return;
        if (absoluteY >= 10 && absoluteY >= absoluteX * 1.73) {
          gesture.axis = "vertical";
          return;
        }
        if (absoluteX < lockDistance || absoluteY > absoluteX * 1.2) return;
        gesture.axis = "horizontal";
        buildTrack(gesture.activeIndex);
        try {
          frame.setPointerCapture(event.pointerId);
        } catch {}
      }

      if (gesture.axis !== "horizontal" || !track) return;
      event.preventDefault();
      recordGestureSamples(gesture, event);
      gesture.deltaX = deltaX;
      gesture.deltaY = deltaY;
      const direction = deltaX < 0 ? 1 : -1;
      const targetIndex = gesture.activeIndex + direction;
      const hasTarget = targetIndex >= 0 && targetIndex < controls.length;
      const translatedX = hasTarget ? deltaX : deltaX * edgeResistance;
      track.style.transform = `translate3d(${translatedX}px, 0, 0)`;
    }, { passive: false });

    frame.addEventListener("pointerup", (event) => finishGesture(event));
    frame.addEventListener("pointercancel", (event) => {
      finishGesture(event, gesture?.axis !== "horizontal", true);
    });
    frame.addEventListener("lostpointercapture", (event) => {
      if (event.target !== frame) return;
      if (gesture && event.pointerId === gesture.pointerId) resetGesture();
    });
  };

  const initializeGalleryRailHint = (gallery) => {
    const rail = gallery.querySelector(".el-gallery-controls");
    const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches;
    if (!rail || !coarsePointer || reducedMotion || reducedData || !("IntersectionObserver" in window)) return;

    let cancelled = false;
    let animation = null;
    const cancel = () => {
      cancelled = true;
      animation?.cancel?.();
      observer.disconnect();
      rail.style.removeProperty("scroll-snap-type");
    };
    ["pointerdown", "wheel", "keydown", "touchstart"].forEach((eventName) => {
      gallery.addEventListener(eventName, cancel, { once: true, passive: eventName !== "keydown" });
    });
    gallery.addEventListener("eagleline:variant-change", (event) => {
      if (event.detail?.userInitiated) cancel();
    });

    const observer = new IntersectionObserver((entries) => {
      if (cancelled || !entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .35)) return;
      observer.disconnect();
      if (rail.scrollWidth <= rail.clientWidth + 4 || typeof rail.animate !== "function") return;
      rail.style.setProperty("scroll-snap-type", "none");
      animation = rail.animate(
        [
          { transform: "translate3d(0, 0, 0)", easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(-18px, 0, 0)", offset: .22, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(0, 0, 0)", offset: .43, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(-14px, 0, 0)", offset: .7, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(0, 0, 0)" },
        ],
        { duration: 1400, delay: 350, easing: "linear", iterations: 1 },
      );
      const restoreSnap = () => rail.style.removeProperty("scroll-snap-type");
      if (animation.finished?.finally) animation.finished.finally(restoreSnap).catch(() => {});
      else window.setTimeout(restoreSnap, 1900);
    }, { threshold: [.35] });
    observer.observe(rail);
  };

  const primeGalleryMain = (gallery, { urgent = false } = {}) => {
    const mainImage = gallery?.querySelector("[data-gallery-main]");
    const activeControl = gallery?.querySelector('[data-gallery-thumb][aria-pressed="true"]');
    if (!mainImage || !activeControl) return Promise.resolve();
    mainImage.loading = "eager";
    if ("fetchPriority" in mainImage) mainImage.fetchPriority = urgent ? "high" : "low";
    return loadGalleryImage(galleryControlSources(activeControl));
  };

  const productGallery = document.querySelector("[data-product-gallery][data-gallery]");
  if (productGallery) {
    const primeFromIntent = () => {
      primeGalleryMain(productGallery, { urgent: true })
        .then(() => {
          if (!reducedData) warmGalleryNeighbors(productGallery);
        })
        .catch(() => {});
    };
    document.querySelectorAll('[data-purchase-cta][data-cta-destination="product_gallery"]').forEach((cta) => {
      cta.addEventListener("pointerdown", primeFromIntent, { passive: true });
      cta.addEventListener("focus", primeFromIntent, { passive: true });
      cta.addEventListener("click", primeFromIntent, { passive: true });
    });

    if (pageType === "home" && !reducedData) {
      const heroImage = document.querySelector('.el-v63-hero img[fetchpriority="high"]');
      const schedulePrime = () => {
        const run = () => primeGalleryMain(productGallery).catch(() => {});
        if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1200 });
        else window.setTimeout(run, 120);
      };
      if (typeof heroImage?.decode === "function") {
        heroImage.decode().catch(() => {}).then(schedulePrime);
      } else if (heroImage?.complete) schedulePrime();
      else heroImage?.addEventListener("load", schedulePrime, { once: true });
    }
  }

  document.querySelectorAll("[data-gallery]").forEach((gallery) => {
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    const mainVideo = gallery.querySelector("[data-gallery-main-video]");
    const videoToggle = gallery.querySelector("[data-gallery-video-toggle]");
    controls.forEach((button) => {
      button.disabled = false;
      button.addEventListener("click", (event) => {
        selectGalleryControl(gallery, button, event, { interactionSource: "thumbnail" });
      });
    });
    if (mainVideo && videoToggle) {
      ["play", "pause", "ended"].forEach((eventName) => {
        mainVideo.addEventListener(eventName, () => syncGalleryVideoToggle(mainVideo, videoToggle));
      });
      videoToggle.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const activeControl = controls.find((control) => control.getAttribute("aria-pressed") === "true");
        if (!activeControl || activeControl.dataset.galleryMediaType !== "video") return;
        if (mainVideo.paused) {
          mainVideo.play().then(() => {
            if (event.isTrusted) {
              publishGalleryMediaEvent(gallery, activeControl, "video_control", "video_play");
            }
          }).catch(() => {});
          return;
        }
        mainVideo.pause();
        if (event.isTrusted) {
          publishGalleryMediaEvent(gallery, activeControl, "video_control", "video_pause");
        }
      });
      syncGalleryVideoToggle(mainVideo, videoToggle);
    }
    initializeGallerySwipe(gallery, controls);
    initializeGalleryRailHint(gallery);
    if (reducedData) return;
    const warm = () => warmGalleryNeighbors(gallery);
    let warmScheduled = false;
    const scheduleWarm = () => {
      if (warmScheduled) return;
      warmScheduled = true;
      const runWhenIdle = () => {
        window.requestAnimationFrame(() => {
          if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 1500 });
          else window.setTimeout(warm, 0);
        });
      };
      const mainImage = gallery.querySelector("[data-gallery-main]");
      if (typeof mainImage?.decode === "function") {
        mainImage.decode().catch(() => {}).then(runWhenIdle);
      } else runWhenIdle();
    };
    if (!("IntersectionObserver" in window)) {
      scheduleWarm();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      scheduleWarm();
    }, { rootMargin: "400px 0px" });
    observer.observe(gallery);
  });


  document.querySelectorAll("[data-mechanism-switcher]").forEach((switcher) => {
    const panels = [...switcher.querySelectorAll("video[data-mechanism-panel]")];
    const tabs = [...switcher.querySelectorAll("[data-mechanism-tab]")];
    const label = switcher.querySelector("[data-mechanism-label]");
    const summaryTitle = switcher.querySelector("[data-mechanism-summary-title]");
    const summaryCopy = switcher.querySelector("[data-mechanism-summary-copy]");
    const toggle = switcher.querySelector("[data-mechanism-toggle]");
    const toggleText = switcher.querySelector("[data-mechanism-toggle-text]");
    const toggleIcon = switcher.querySelector("[data-mechanism-toggle-icon]");
    const status = switcher.querySelector("[data-mechanism-status]");
    if (!panels.length || panels.length !== tabs.length) return;

    tabs.forEach((tab) => { tab.disabled = false; });
    panels.forEach((panel) => {
      panel.muted = true;
      panel.loop = true;
      panel.playsInline = true;
      panel.removeAttribute("controls");
      panel.pause();
    });
    if (toggle) {
      toggle.hidden = false;
      toggle.disabled = false;
    }

    let activeIndex = 0;
    let inView = false;
    let playbackEnabled = !reducedMotion && !reducedData;
    let reducedMotionOverride = false;
    const currentPanel = () => panels[activeIndex];
    const playbackRequested = () =>
      playbackEnabled && (!reducedMotion || reducedMotionOverride);

    const announce = (message) => {
      if (status) status.textContent = message;
    };
    const syncToggle = () => {
      const enabled = playbackRequested();
      const text = enabled
        ? themeString("mechanism_pause_label", "Pause the mechanism video")
        : themeString("mechanism_play_label", "Play the mechanism video");
      toggle?.setAttribute("aria-label", text);
      toggle?.setAttribute("data-media-playback", enabled ? "enabled" : "paused");
      if (toggleText) toggleText.textContent = enabled
        ? themeString("pause", "Pause")
        : themeString("play", "Play");
      if (toggleIcon) toggleIcon.textContent = enabled ? "Ⅱ" : "▶";
    };
    const syncPlayingState = () => {
      toggle?.setAttribute("data-media-playing", String(!currentPanel().paused));
    };
    const playCurrent = () => {
      const panel = currentPanel();
      if (playbackRequested() && !document.hidden && inView && isVisible(panel)) safePlay(panel);
      else panel.pause();
      syncToggle();
    };
    const activate = (index) => {
      activeIndex = (index + panels.length) % panels.length;
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === activeIndex;
        panel.pause();
        panel.classList.toggle("is-current", active);
        panel.setAttribute("aria-hidden", String(!active));
        panel.tabIndex = -1;
      });
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === activeIndex;
        tab.classList.toggle("is-current", active);
        tab.closest("li")?.classList.toggle("is-current", active);
        tab.setAttribute("aria-pressed", String(active));
        tab.removeAttribute("aria-selected");
        tab.tabIndex = 0;
      });
      if (label) label.textContent = tabs[activeIndex].dataset.mechanismLabel || tabs[activeIndex].textContent.trim();
      if (summaryTitle) summaryTitle.textContent = tabs[activeIndex].querySelector("strong")?.textContent || "";
      if (summaryCopy) summaryCopy.textContent = tabs[activeIndex].querySelector("span")?.textContent || "";
      playCurrent();
      syncPlayingState();
      syncToggle();
      document.dispatchEvent(new CustomEvent("eagleline:media-state"));
    };

    panels.forEach((panel) => {
      panel.addEventListener("play", syncPlayingState);
      panel.addEventListener("pause", syncPlayingState);
    });
    tabs.forEach((tab, index) => tab.addEventListener("click", (event) => {
      if (index === activeIndex) return;
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "gallery_select",
        });
      }
      activate(index);
    }));
    toggle?.addEventListener("click", (event) => {
      const enablePlayback = !playbackRequested();
      playbackEnabled = enablePlayback;
      reducedMotionOverride = enablePlayback && reducedMotion;
      if (!enablePlayback) panels.forEach((panel) => panel.pause());
      else playCurrent();
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: enablePlayback ? "video_play" : "video_pause",
        });
      }
      announce(enablePlayback
        ? themeString("mechanism_playing", "The mechanism video is playing.")
        : themeString("mechanism_paused", "The mechanism video is paused."));
      syncPlayingState();
      syncToggle();
    });
    observeVisibility(
      switcher,
      (visible) => {
        inView = visible;
        if (visible) playCurrent();
        else panels.forEach((panel) => panel.pause());
        syncToggle();
      },
      { threshold: 0.32 },
    );
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) panels.forEach((panel) => panel.pause());
      else playCurrent();
      syncToggle();
    });
    document.addEventListener("eagleline:media-state", playCurrent);
    switcher.addEventListener("keydown", (event) => {
      if (!tabs.includes(document.activeElement)) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const index = (tabs.indexOf(document.activeElement) + direction + tabs.length) % tabs.length;
      tabs[index].focus();
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "gallery_select",
        });
      }
      activate(index);
    });
    onMediaChange(motionPreference, (event) => {
      reducedMotionOverride = false;
      playbackEnabled = !event.matches && !reducedData;
      if (event.matches) {
        panels.forEach((panel) => panel.pause());
      } else playCurrent();
      announce(
        event.matches
          ? themeString("mechanism_reduced_motion", "The mechanism video is paused for reduced motion.")
          : reducedData
            ? themeString("mechanism_reduced_data", "Mechanism autoplay is off for reduced data.")
            : themeString("mechanism_motion_resumed", "The mechanism video is playing after reduced motion was turned off."),
      );
      syncPlayingState();
      syncToggle();
    });
    activate(0);
  });

  document.querySelectorAll("[data-product-form]").forEach((root) => {
    const productScript = root.querySelector("[data-product-json]");
    if (!productScript) return;

    let product;
    try {
      product = JSON.parse(productScript.textContent);
    } catch {
      return;
    }

    const form = root.querySelector("form");
    const variantSelector = root.querySelector("[data-variant-id]");
    const price = root.querySelector("[data-product-price]");
    const priceEstimate = root.querySelector("[data-product-price-estimate]");
    const status = root.querySelector("[data-variant-status]");
    const error = root.querySelector("[data-product-error]");
    const submit = root.querySelector("[data-add-to-cart]");
    const submitText = root.querySelector("[data-add-to-cart-text]");
    const acceleratedCheckout = root.querySelector("[data-accelerated-checkout]");
    const selectionGate = root.querySelector("[data-selection-gate]");
    const selectionToast = root.querySelector("[data-selection-toast]");
    const gallery = root.closest(".el-product-grid")?.querySelector("[data-gallery]");
    const optionControls = root.querySelector("[data-option-controls]");
    const optionButtons = [...root.querySelectorAll("[data-option-control]")];
    const quantityInput = form?.elements?.quantity;
    const strings = product.strings || {};
    const locale = product.locale || document.documentElement.lang || undefined;
    const currency = product.currency || "";
    let currencyFormatter = null;
    try {
      if (currency) currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency });
    } catch {
      currencyFormatter = null;
    }
    const money = (cents, formatted = "") => (
      formatted
      || currencyFormatter?.format(Number(cents || 0) / 100)
      || String(Number(cents || 0) / 100)
    );
    const renderString = (template, replacements = {}) => Object.entries(replacements).reduce(
      (result, [token, value]) => result.replaceAll(token, () => String(value)),
      template || "",
    );
    const canonicalPack = (value) => value === "one_pack" || value === "two_pack" ? value : "";
    const canonicalColor = (value) => value === "black" || value === "white" ? value : "";
    let selectedPackKey = null;
    let selectedColorKey = null;
    let internalVariantChange = false;
    let pendingSelectionEvents = new Map();
    let trustedSubmitAttempt = false;
    let checkoutHighlighted = false;
    let selectionToastTimer = 0;
    let selectionToastCleanup = 0;

    if (variantSelector && status?.id) variantSelector.setAttribute("aria-describedby", status.id);
    if (variantSelector && optionControls && optionButtons.length) {
      optionControls.hidden = false;
      variantSelector.closest(".el-option-group")?.classList.add("has-options");
      variantSelector.tabIndex = -1;
      variantSelector.hidden = true;
      variantSelector.required = false;
      if (form) form.noValidate = true;
    }

    const findVariant = () =>
      product.variants.find((variant) => String(variant.id) === variantSelector?.value);
    const findVariantByKeys = (packKey, colorKey) =>
      product.variants.find((variant) => variant.packKey === packKey && variant.colorKey === colorKey);
    const selectionPrompt = () => {
      if (!selectedPackKey && !selectedColorKey) return strings.selectionPrompt || "";
      if (!selectedPackKey) return strings.selectionPack || "";
      if (!selectedColorKey) return strings.selectionColor || "";
      return strings.selectionCheck || "";
    };

    const clearError = () => {
      if (!error) return;
      error.hidden = true;
      error.textContent = "";
    };

    const showError = (message) => {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      error.focus();
    };

    const showSelectionToast = () => {
      if (!selectionToast) return;
      window.clearTimeout(selectionToastTimer);
      window.clearTimeout(selectionToastCleanup);
      selectionToast.classList.remove("is-visible");
      selectionToast.textContent = "";
      window.requestAnimationFrame(() => {
        selectionToast.textContent = selectionPrompt();
        selectionToast.setAttribute("aria-hidden", "false");
        selectionToast.classList.add("is-visible");
      });
      selectionToastTimer = window.setTimeout(() => {
        selectionToast.classList.remove("is-visible");
        selectionToast.setAttribute("aria-hidden", "true");
        selectionToastCleanup = window.setTimeout(() => {
          selectionToast.textContent = "";
        }, reducedMotion ? 0 : 260);
      }, 2600);
    };

    const syncOptionControls = () => {
      optionButtons.forEach((button) => {
        const name = button.dataset.optionName;
        const value = button.dataset.optionValue;
        const candidatePackKey = name === "pack" ? canonicalPack(value) : selectedPackKey;
        const candidateColorKey = name === "color" ? canonicalColor(value) : selectedColorKey;
        const candidateAvailable = product.variants.some((variant) => (
          variant.available
          && (!candidatePackKey || variant.packKey === candidatePackKey)
          && (!candidateColorKey || variant.colorKey === candidateColorKey)
        ));
        const active = name === "pack" ? value === selectedPackKey : value === selectedColorKey;
        button.classList.toggle("is-selected", active);
        button.setAttribute("aria-pressed", String(active));
        button.disabled = !candidateAvailable;
      });
    };

    const selectGalleryColor = (colorKey, userInitiated = false) => {
      if (!gallery) return;
      const matching = gallery.querySelector(`[data-gallery-primary-color="${canonicalColor(colorKey)}"]`);
      if (matching) {
        selectGalleryControl(gallery, matching, null, {
          autoplay: false,
          customerSelection: Boolean(userInitiated),
          instant: true,
          interactionSource: "color_auto",
        });
      }
    };

    const updateGallery = (variant, changeDetail = null) => {
      if (!gallery) return;
      gallery.dataset.selectedVariantId = String(variant.id);
      gallery.dispatchEvent(
        new CustomEvent("eagleline:variant-change", {
          bubbles: true,
          detail: { variant, userInitiated: Boolean(changeDetail?.userInitiated) },
        }),
      );
    };

    const syncPurchaseState = (variant) => {
      const complete = Boolean(selectedPackKey && selectedColorKey);
      const ready = Boolean(complete && variant?.available);
      root.dataset.selectionReady = String(ready);
      if (acceleratedCheckout) {
        acceleratedCheckout.dataset.variantId = ready ? String(variant.id) : "";
        acceleratedCheckout.setAttribute("aria-disabled", String(!ready));
        acceleratedCheckout.setAttribute("aria-hidden", String(!ready));
        acceleratedCheckout.toggleAttribute("inert", !ready);
        acceleratedCheckout.classList.toggle("is-selection-incomplete", !ready);
      }
      if (selectionGate) {
        selectionGate.hidden = ready;
        selectionGate.setAttribute("aria-label", ready ? "" : complete ? strings.selectionSoldOut || "" : selectionPrompt());
      }
      if (!submit || !submitText) return;
      if (complete && !variant?.available) {
        submit.setAttribute("disabled", "disabled");
        submit.setAttribute("aria-disabled", "true");
        submitText.textContent = strings.soldOut || "";
        return;
      }
      submit.removeAttribute("disabled");
      submit.setAttribute("aria-disabled", String(!ready));
      submit.classList.toggle("is-selection-incomplete", !ready);
      submitText.textContent = strings.addToCart || "";
    };

    const publishPendingSelections = (variant) => {
      if (!variant?.available || !pendingSelectionEvents.size) return;
      const events = [...pendingSelectionEvents.values()];
      pendingSelectionEvents.clear();
      events.forEach((changeDetail) => {
        publishEagleLineEvent("eagleline_option_select", {
          option: changeDetail.option === "pack" ? "pack_size" : "color",
          value: changeDetail.option === "pack"
            ? canonicalPack(changeDetail.value)
            : canonicalColor(changeDetail.value),
          variant_id: String(variant.id),
          pack_size: canonicalPack(variant.packKey),
          color: canonicalColor(variant.colorKey),
        });
      });
    };

    const updateSelection = (changeDetail = null, syncSelector = true) => {
      const variant = selectedPackKey && selectedColorKey
        ? findVariantByKeys(selectedPackKey, selectedColorKey)
        : null;
      const nextVariantId = variant ? String(variant.id) : "";
      if (syncSelector && variantSelector && variantSelector.value !== nextVariantId) {
        internalVariantChange = true;
        variantSelector.value = nextVariantId;
        variantSelector.dispatchEvent(new Event("change", { bubbles: true }));
        internalVariantChange = false;
      }
      syncOptionControls();
      if (status) {
        status.textContent = !selectedPackKey || !selectedColorKey
          ? selectionPrompt()
          : variant?.available
          ? renderString(strings.statusAvailable, {
            __EL_TITLE__: variant.title,
            __EL_PRICE__: money(variant.price, variant.priceFormatted),
          })
          : renderString(strings.statusSoldOut, {
            __EL_TITLE__: variant?.title || strings.selectedCombination || "",
          });
        status.classList.toggle("is-available", Boolean(variant?.available));
      }
      if (variant?.available) {
        if (price) price.textContent = money(variant.price, variant.priceFormatted);
        if (priceEstimate) priceEstimate.textContent = variant.thbEstimate || "";
        updateGallery(variant, changeDetail);
        if (changeDetail?.userInitiated && !checkoutHighlighted && !reducedMotion && acceleratedCheckout?.animate) {
          checkoutHighlighted = true;
          acceleratedCheckout.animate(
            [
              { boxShadow: "0 0 0 0 rgba(7,87,213,0)" },
              { boxShadow: "0 0 0 4px rgba(7,87,213,.18)", offset: .45 },
              { boxShadow: "0 0 0 0 rgba(7,87,213,0)" },
            ],
            { duration: 560, easing: "ease-out", iterations: 1 },
          );
        }
        publishPendingSelections(variant);
      }
      syncPurchaseState(variant);
    };

    variantSelector?.addEventListener("change", () => {
      if (internalVariantChange) return;
      clearError();
      const variant = findVariant();
      selectedPackKey = canonicalPack(variant?.packKey) || null;
      selectedColorKey = canonicalColor(variant?.colorKey) || null;
      pendingSelectionEvents.clear();
      if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
      updateSelection(null, false);
    });
    optionButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        if (!variantSelector || button.disabled) return;
        const option = button.dataset.optionName;
        const value = button.dataset.optionValue;
        const currentValue = option === "pack" ? selectedPackKey : selectedColorKey;
        if (value === currentValue) return;
        clearError();
        if (option === "pack") selectedPackKey = canonicalPack(value) || null;
        else selectedColorKey = canonicalColor(value) || null;
        const changeDetail = { option, value, userInitiated: event.isTrusted };
        if (event.isTrusted) pendingSelectionEvents.set(option, changeDetail);
        if (option === "color") selectGalleryColor(value, event.isTrusted);
        updateSelection(changeDetail);
      });
    });
    const initialVariant = findVariant();
    selectedPackKey = canonicalPack(initialVariant?.packKey) || null;
    selectedColorKey = canonicalColor(initialVariant?.colorKey) || null;
    if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
    updateSelection(null, false);

    window.addEventListener("pageshow", () => {
      window.requestAnimationFrame(() => {
        const restoredVariant = findVariant();
        const selectedVariant = selectedPackKey && selectedColorKey
          ? findVariantByKeys(selectedPackKey, selectedColorKey)
          : null;
        if ((restoredVariant?.id || null) === (selectedVariant?.id || null)) return;
        selectedPackKey = canonicalPack(restoredVariant?.packKey) || null;
        selectedColorKey = canonicalColor(restoredVariant?.colorKey) || null;
        pendingSelectionEvents.clear();
        if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
        updateSelection(null, false);
      });
    });

    const localPreview = form?.dataset.localPreview === "true";
    quantityInput?.addEventListener("invalid", (event) => {
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_cart_error", { error_category: "invalid_quantity" });
      }
      trustedSubmitAttempt = false;
    });
    submit?.addEventListener("click", (event) => {
      trustedSubmitAttempt = event.isTrusted;
    });
    selectionGate?.addEventListener("click", (event) => {
      showSelectionToast();
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_cart_error", { error_category: "invalid_selection" });
      }
    });
    form?.addEventListener("keydown", (event) => {
      if (event.isTrusted && event.key === "Enter") trustedSubmitAttempt = true;
    });
    form?.addEventListener("submit", (event) => {
      if (event.submitter !== submit || root.dataset.selectionReady === "true") return;
      const customerAttempt = trustedSubmitAttempt;
      trustedSubmitAttempt = false;
      event.preventDefault();
      showSelectionToast();
      if (customerAttempt) {
        publishEagleLineEvent("eagleline_cart_error", { error_category: "invalid_selection" });
      }
    });
    if (
      !form ||
      localPreview ||
      form.method.toLowerCase() !== "post" ||
      !("fetch" in window) ||
      !("FormData" in window)
    ) return;

    form.addEventListener("submit", async (event) => {
      if (event.submitter !== submit) return;
      if (event.defaultPrevented) return;
      const customerAttempt = trustedSubmitAttempt;
      trustedSubmitAttempt = false;
      const variant = findVariant();
      if (!variant?.available) {
        event.preventDefault();
        showError(strings.unavailableError || "");
        if (customerAttempt) {
          publishEagleLineEvent("eagleline_cart_error", { error_category: "unavailable_variant" });
        }
        return;
      }
      const quantity = Number(form.elements.quantity?.value);
      if (!Number.isInteger(quantity) || quantity < 1) {
        event.preventDefault();
        showError(strings.invalidQuantity || "");
        if (customerAttempt) {
          publishEagleLineEvent("eagleline_cart_error", { error_category: "invalid_quantity" });
        }
        return;
      }
      event.preventDefault();
      clearError();
      form.setAttribute("aria-busy", "true");
      submit?.setAttribute("disabled", "disabled");
      if (submitText) submitText.textContent = strings.adding || "";

      let failureCategory = "network_failure";
      try {
        const base = product.cartAddUrl || form.action;
        const endpoint = base.endsWith(".js") ? base : `${base}.js`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: new FormData(form),
        });
        failureCategory = "cart_request_failed";
        const contentType = response.headers.get("content-type") || "";
        const result = contentType.includes("json") ? await response.json() : {};
        if (!response.ok) throw new Error(result.description || strings.cartError || "");
        if (customerAttempt) {
          publishEagleLineEvent("eagleline_cart_add_success", {
            variant_id: String(variant.id),
            pack_size: canonicalPack(variant.packKey),
            color: canonicalColor(variant.colorKey),
            quantity,
            page_type: pageType,
          });
        }
        window.location.assign(product.cartUrl || "/cart");
      } catch (requestError) {
        showError(requestError.message || strings.cartRetryError || "");
        if (customerAttempt) {
          publishEagleLineEvent("eagleline_cart_error", { error_category: failureCategory });
        }
        form.removeAttribute("aria-busy");
        updateSelection();
      }
    });
  });

  /* FAQ accordion: a smooth height tween on top of the native <details>
     toggle. No-JS keeps the instant native behavior; reduced motion skips
     the controller entirely. The .is-animating class clips the box while
     the tween runs. */
  if (!reducedMotion) {
    document.querySelectorAll(".el-v6-support-list details").forEach((details) => {
      const summary = details.querySelector("summary");
      if (!summary) return;
      summary.addEventListener("click", (event) => {
        event.preventDefault();
        if (details.dataset.animating) return;
        const summaryHeight = summary.offsetHeight;
        const options = { duration: 320, easing: "cubic-bezier(.22, 1, .36, 1)" };
        details.dataset.animating = "1";
        details.classList.add("is-animating");
        if (details.open) {
          const start = details.offsetHeight;
          const animation = details.animate({ height: [start + "px", summaryHeight + "px"] }, options);
          animation.onfinish = () => {
            details.open = false;
            delete details.dataset.animating;
            details.classList.remove("is-animating");
          };
        } else {
          details.open = true;
          const end = details.offsetHeight;
          const animation = details.animate({ height: [summaryHeight + "px", end + "px"] }, options);
          animation.onfinish = () => {
            delete details.dataset.animating;
            details.classList.remove("is-animating");
          };
        }
      });
    });
  }
  };

  /* On compact screens, let the responsive hero image reach first paint
     before initializing below-fold motion and controls. Essential content,
     links, and native form fallbacks already work in the HTML. */
  const compactBoot = window.matchMedia("(max-width: 920px)").matches;
  if (compactBoot) {
    window.requestAnimationFrame(() => window.setTimeout(initialize, 0));
  } else {
    initialize();
  }
})();
